/** 
 * © 2025 COMPUTACIÓN EN ACCIÓN, S.A. DE C.V. 'CONTPAQi'. Todos los derechos reservados. 
 * Este código fuente constituye una obra original protegida por la legislación nacional e internacional en materia de propiedad intelectual. 
 * Su reproducción, distribución, comunicación pública, transformación, descompilación, ingeniería inversa o cualquier uso no autorizado, 
 * total o parcial, se encuentra expresamente prohibido sin la previa autorización por escrito de COMPUTACIÓN EN ACCIÓN, S.A. DE C.V. 
 * El uso indebido de este software puede ser constitutivo de delitos y dará lugar a las responsabilidades civiles y penales correspondientes. 
*/ 
 
import {put, takeEvery, select, call} from '@redux-saga/core/effects';
import posthog from 'posthog-js';
import {
  deleteRolesFailure,
  deleteRolesSuccess,
  getRolesFailure,
  getRolesSuccess,
} from '../actions/roles.action';
import {
  GET_ROLES,
  DELETE_ROL,
  GetRoles
} from 'src/types/roles.type';
import { getPrettierMessage } from 'src/utils/global';
import { Messages } from 'src/constants/message.constant';
import { ApiRbacUrl } from 'src/utils/config';
import { ApiRbacPaths } from 'src/constants/endpoints.constant';
import { requestApi, parseResponse } from 'src/providers/http-api.provider';
import Role from 'src/models/role.model';
import { setToasNotification } from '../actions/ui-notification.action';
import { NotificationTypes } from 'src/constants/theme.constant';

function* onGetRoles(data: GetRoles):any {
  try {
    const platformToken = yield select(state => state.config?.platformToken);
    const licenseId = yield select(state => state.config?.licenseId);
    const serviceId =  data.payload?.serviceId;
    if (platformToken && licenseId && serviceId) {
      const endpoint = `${ApiRbacUrl}${ApiRbacPaths.BASE}/${serviceId}/licenses/${licenseId}/roles`;
      let response = yield call(requestApi, {
        endpoint,
        method: 'GET',
        headers: {
          Authorization: platformToken,
        },
        useParseResponse: false,
      });
      if (response?.ok) {
        let responseData = yield call(parseResponse, response);
        const listRolModel = new Role();
        const roles = listRolModel.getListRoleModel(responseData);
        let rolesData:any = {
          licenseId,
          serviceId,
        }
        if (roles.length > 0) {
          rolesData['roles'] = roles;
          yield put(getRolesSuccess(rolesData));
        } else {
          rolesData['roles'] = [];
          yield put(getRolesSuccess(rolesData));
        }
      } else {
        const apiError = yield call(parseResponse, response);
        throw new Error(getPrettierMessage(apiError?.detail));
      }
    } else {
      throw new Error(Messages.PARAMS_EMPTY);
    }
  } catch (err:any) {
    console.log("Fail onGetRolesLicenses: ", err);
    yield put(getRolesFailure(err?.message || Messages.ROL.GET_ERROR));
  }
}

function* onDeleteRoles(data: GetRoles):any {
  try {
    const platformToken = yield select(state => state.config?.platformToken);
    const currentRoles = yield select(state => state.roles?.data);
    const serviceId =  yield select(state => state.config?.serviceId);
    const licenseId = yield select(state => state.config?.licenseId);
    const userId = yield select(state => state.config?.identityId);
    const roleId = data?.payload?.roleId;

    if (platformToken && serviceId && licenseId && roleId) {
      const endpoint = `${ApiRbacUrl}${ApiRbacPaths.BASE}/${serviceId}/licenses/${licenseId}/roles/${roleId}`;
      let response = yield call(requestApi, {
        endpoint,
        method: 'DELETE',
        headers: {
          Authorization: platformToken,
        },
        useParseResponse: false,
      });
      if (response?.ok) {
        let updatedRoles = currentRoles.filter((rol:any) => rol.roleId !== roleId);
        let deletedRol = currentRoles.find((rol:any) => rol.roleId === roleId);
        let payloadRoles = {
          roles: updatedRoles,
          licenseId,
          serviceId,
        }
        yield put(deleteRolesSuccess(payloadRoles));
        yield put(setToasNotification({
          type: NotificationTypes.Success,
          message: Messages.ROL.GENERAL_TITLE_NOTIFICATION,
          description: Messages.ROL.DELETE_SUCCESS,
        }));
        if (posthog) {
          let rolePostHogData = {
            roleId: deletedRol.roleId,
            roleName: deletedRol.roleName,
            serviceId,
            modifiedBy: userId,
            licenseId
          };
          posthog.capture('role_deleted', rolePostHogData);
        }
      } else {
        const apiError = yield call(parseResponse, response);
        throw new Error(getPrettierMessage(apiError?.detail));
      }
    } else {
      throw new Error(Messages.PARAMS_EMPTY);
    }
  } catch (err:any) {
    console.log("Fail onDeleteRoleInLicense: ", err);
    yield put(deleteRolesFailure(err?.message || Messages.ROL.DELETE_ERROR));
  }
}

export function* watchOnGetRoles() {
  yield takeEvery(GET_ROLES, onGetRoles);
}

export function* watchOnDeleteRoles() {
  yield takeEvery(DELETE_ROL, onDeleteRoles);
}
